import { Component, EventEmitter, Input, Output } from '@angular/core';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import {
  LexgosignEnvelope,
  LexgosignRecipient,
  LexgosignService
} from '@services/lexgosign.service';
import { LexgosignSignComponent } from '../pop-up/lexgosign-sign/lexgosign-sign.component';
import { LexgosignProgressComponent } from '../pop-up/lexgosign-progress/lexgosign-progress.component';
import { ToastService } from '../toast/toast-service';
import { ConfirmActionModalComponent } from '../components/confirm-action-modal/confirm-action-modal.component';

@Component({
  selector: 'app-lexgosign-detail',
  templateUrl: './lexgosign-detail.component.html',
  styleUrls: ['./lexgosign-detail.component.scss'],
  standalone: false
})
export class LexgosignDetailComponent {
  private _envelopes: LexgosignEnvelope[] = [];

  @Input()
  set envelopes(value: LexgosignEnvelope[] | null | undefined) {
    this._envelopes = Array.isArray(value) ? value : [];
  }
  get envelopes(): LexgosignEnvelope[] {
    return this._envelopes;
  }

  @Output() refresh = new EventEmitter<void>();

  busyIds = new Set<string>();

  constructor(
    private modalService: NgbModal,
    private lexgosignService: LexgosignService,
    private toastService: ToastService
  ) {}

  statusLabel(status: string): string {
    const st = (status || '').toUpperCase();
    // Misma lógica que la grilla Estatus firmas
    if (st === 'CREATED' || st === 'SENDING' || st === 'ERROR') return 'Pendiente';
    if (st === 'SENT' || st === 'IN_PROGRESS') return 'En Firma';
    if (st === 'SUCCESS') return 'Firmado';
    if (st === 'VOIDED') return 'Anulado';
    return status || '';
  }

  recipientStatusLabel(status: string): string {
    const st = (status || '').toUpperCase();
    switch (st) {
      case 'SIGNED':
        return 'Firmado';
      case 'REJECTED':
        return 'Rechazado';
      case 'IN_PROGRESS':
      case 'EMAIL_SENT':
      case 'EMAIL_RECEIVED':
      case 'EMAIL_OPENED':
        return 'En Firma';
      case 'PENDING':
      case 'CREATED':
      case 'SENDING':
        return 'Pendiente';
      default:
        return status || 'Pendiente';
    }
  }

  statusClass(status: string): string {
    const st = (status || '').toUpperCase();
    if (st === 'SUCCESS') return 'badge-success';
    if (st === 'SENT' || st === 'IN_PROGRESS') return 'badge-info';
    if (st === 'VOIDED') return 'badge-secondary';
    if (st === 'CREATED' || st === 'SENDING' || st === 'ERROR') return 'badge-warning';
    return 'badge-warning';
  }

  signedCount(envelope: LexgosignEnvelope): number {
    return this.recipientsOf(envelope).filter((r) => r.status === 'SIGNED').length;
  }

  recipientsOf(envelope: LexgosignEnvelope): LexgosignRecipient[] {
    return Array.isArray(envelope?.recipients) ? envelope.recipients : [];
  }

  hasPending(envelope: LexgosignEnvelope): boolean {
    return this.recipientsOf(envelope).some(
      (r) => r.status !== 'SIGNED' && r.status !== 'REJECTED'
    );
  }

  canRemind(envelope: LexgosignEnvelope): boolean {
    return (
      !!envelope?.id &&
      this.hasPending(envelope) &&
      envelope.status !== 'SUCCESS' &&
      envelope.status !== 'VOIDED'
    );
  }

  canVoid(envelope: LexgosignEnvelope): boolean {
    return (
      !!envelope?.id &&
      envelope.status !== 'SUCCESS' &&
      envelope.status !== 'VOIDED'
    );
  }

  canEvidence(envelope: LexgosignEnvelope): boolean {
    return !!envelope?.id && (envelope.status === 'SUCCESS' || !!envelope.evidenceUrl);
  }

  canSign(recipient: LexgosignRecipient): boolean {
    return !!recipient?.signingUrl && recipient.status !== 'SIGNED' && recipient.status !== 'REJECTED';
  }

  canSignAny(envelope: LexgosignEnvelope): boolean {
    return this.recipientsOf(envelope).some((r) => this.canSign(r));
  }

  isBusy(id: string | number): boolean {
    return this.busyIds.has(String(id));
  }

  private setBusy(id: string | number, busy: boolean) {
    const key = String(id);
    if (busy) {
      this.busyIds.add(key);
    } else {
      this.busyIds.delete(key);
    }
  }

  sendReminder(envelope: LexgosignEnvelope) {
    if (!this.canRemind(envelope) || this.isBusy(envelope.id)) {
      return;
    }
    this.setBusy(envelope.id, true);
    this.lexgosignService.remind(envelope.id).subscribe({
      next: (resp: any) => {
        const inner = resp?.result ?? resp;
        const msg =
          resp?.message ||
          inner?.message ||
          'Recordatorio enviado a los firmantes pendientes';
        if (resp?.success === false) {
          this.toastService.show(resp.message || msg, {
            classname: 'bg-danger text-light',
            delay: 5000
          });
        } else {
          this.toastService.show(msg, { classname: 'bg-success text-light', delay: 4000 });
        }
        this.setBusy(envelope.id, false);
      },
      error: (err) => {
        const message =
          err?.error?.message || err?.message || 'No se pudo enviar el recordatorio';
        this.toastService.show(message, { classname: 'bg-danger text-light', delay: 5000 });
        this.setBusy(envelope.id, false);
      }
    });
  }

  viewProgress(envelope: LexgosignEnvelope) {
    const modalRef = this.modalService.open(LexgosignProgressComponent, {
      size: 'md',
      scrollable: true,
      backdrop: 'static'
    });
    modalRef.componentInstance.envelopePk = envelope.id;
    modalRef.componentInstance.envelopeName = envelope.name || envelope.attachmentName || '';
    modalRef.result.then(
      () => this.refresh.emit(),
      () => this.refresh.emit()
    );
  }

  openEvidence(envelope: LexgosignEnvelope) {
    if (envelope.evidenceUrl) {
      window.open(envelope.evidenceUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    if (!this.canEvidence(envelope) || this.isBusy(envelope.id)) {
      return;
    }
    this.setBusy(envelope.id, true);
    this.lexgosignService.evidence(envelope.id).subscribe({
      next: (resp: any) => {
        this.setBusy(envelope.id, false);
        if (resp?.success === false) {
          this.toastService.show(resp.message || 'No se pudo obtener la evidencia', {
            classname: 'bg-danger text-light',
            delay: 5000
          });
          return;
        }
        const url = resp?.result?.evidenceUrl || resp?.evidenceUrl;
        if (url) {
          window.open(url, '_blank', 'noopener,noreferrer');
          this.refresh.emit();
        } else {
          this.toastService.show('La hoja de evidencia aún no está disponible', {
            classname: 'bg-warning text-dark',
            delay: 4000
          });
        }
      },
      error: (err) => {
        this.setBusy(envelope.id, false);
        const message =
          err?.error?.message || err?.message || 'No se pudo obtener la hoja de evidencia';
        this.toastService.show(message, { classname: 'bg-danger text-light', delay: 5000 });
      }
    });
  }

  voidEnvelope(envelope: LexgosignEnvelope) {
    if (!this.canVoid(envelope) || this.isBusy(envelope.id)) {
      return;
    }
    const confirmModal = this.modalService.open(ConfirmActionModalComponent, {
      size: 'sm',
      centered: true
    });
    confirmModal.componentInstance.titel = 'Anular firma';
    confirmModal.componentInstance.message = `¿Anular la firma de "${
      envelope.name || envelope.attachmentName
    }"? Esta acción no se puede deshacer.`;
    confirmModal.result
      .then((confirmed) => {
        if (!confirmed) return;
        this.setBusy(envelope.id, true);
        this.lexgosignService.void(envelope.id).subscribe({
          next: (resp: any) => {
            this.setBusy(envelope.id, false);
            if (resp?.success === false) {
              this.toastService.show(resp.message || 'No se pudo anular', {
                classname: 'bg-danger text-light',
                delay: 5000
              });
              return;
            }
            this.toastService.show('Firma anulada correctamente', {
              classname: 'bg-success text-light',
              delay: 4000
            });
            this.refresh.emit();
          },
          error: (err) => {
            this.setBusy(envelope.id, false);
            const message =
              err?.error?.message || err?.message || 'No se pudo anular la firma';
            this.toastService.show(message, {
              classname: 'bg-danger text-light',
              delay: 5000
            });
          }
        });
      })
      .catch(() => {});
  }

  openSign(envelope: LexgosignEnvelope, recipient?: LexgosignRecipient) {
    const target =
      recipient ||
      this.recipientsOf(envelope).find((r) => this.canSign(r)) ||
      this.recipientsOf(envelope)[0];
    if (!target?.signingUrl) {
      return;
    }
    const modalRef = this.modalService.open(LexgosignSignComponent, {
      size: 'xl',
      scrollable: true,
      backdrop: 'static',
      keyboard: true,
      windowClass: 'lexgosign-sign-modal'
    });
    modalRef.componentInstance.signingUrl = target.signingUrl;
    modalRef.componentInstance.recipientName = target.name || target.email || '';
    modalRef.componentInstance.documentName = envelope.name || envelope.attachmentName || '';
    modalRef.result.then(
      () => this.refresh.emit(),
      () => this.refresh.emit()
    );
  }
}
